import { useMemo } from 'react';

import {
  FiActivity,
  FiBookOpen,
  FiClipboard,
  FiCompass,
  FiDollarSign,
  FiFileText,
  FiGrid,
  FiHome,
  FiInfo,
  FiTarget,
} from 'react-icons/fi';

import { useAuth } from './use-auth';
import { useAccessStatus } from './use-subscription-status';
import { useGetProfileQuery } from '../store/queries/users';

/**
 * The single definition of the primary navigation, used by both the desktop
 * horizontal menu and the mobile drawer. The two listed the same destinations
 * separately before, so anything added to one silently went missing from the
 * other.
 *
 * `Icon` is the component rather than a rendered element, which keeps this a
 * plain `.js` module: the drawer sizes it, the horizontal menu drops it.
 */
export const useMainNavItems = () => {
  const { isAuthenticated } = useAuth();
  const { isOnboardingComplete } = useAccessStatus();
  const { data: profileData } = useGetProfileQuery(undefined, { skip: !isAuthenticated });

  const isUserNav = isAuthenticated && isOnboardingComplete;

  /*
   * Dismissing Getting Started hides it everywhere else: it writes
   * `gettingStartedDismissed`, which stops the route guard offering it and
   * hides the dashboard checklist. The button says "Skip for now", so this is
   * the way back until the screen is genuinely finished — which only happens
   * by choosing somewhere to go next, after at least one action.
   */
  const showGettingStarted = isUserNav && !profileData?.firstTimeProgress?.gettingStartedCompleted;

  return useMemo(
    () =>
      [
        !isAuthenticated && { key: 'home', label: 'Home', link: '/', Icon: FiHome },
        showGettingStarted && {
          key: 'getting-started',
          label: 'Getting Started',
          link: '/user/getting-started',
          Icon: FiCompass,
        },
        isUserNav && { key: 'dashboard', label: 'Dashboard', link: '/user/dashboard', Icon: FiGrid },
        isUserNav && { key: 'plan', label: 'My Plan', link: '/user/plan', Icon: FiClipboard },
        isUserNav && { key: 'workouts', label: 'Workouts', link: '/user/workouts', Icon: FiActivity },
        isUserNav && { key: 'recipes', label: 'Recipes', link: '/user/recipes', Icon: FiBookOpen },
        isUserNav && { key: 'tracking', label: 'Tracking', link: '/user/tracking', Icon: FiTarget },
        { key: 'pricing', label: 'Pricing', link: '/pricing', Icon: FiDollarSign },
        !isOnboardingComplete && { key: 'about', label: 'About', link: '/about', Icon: FiInfo },
        !isOnboardingComplete && { key: 'blog', label: 'Blog', link: '/blog', Icon: FiFileText },
      ].filter(Boolean),
    [isAuthenticated, isOnboardingComplete, isUserNav, showGettingStarted],
  );
};
